import {
  type CreateCustomerNoteInput,
  type CustomerNote,
  createCustomerNoteSchema,
  queryKeys,
} from '@mevaser/shared'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Switch,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Pencil, Pin, PinOff, Plus, StickyNote, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { api } from '../lib/api.js'
import { LinkifiedText } from './linkified-text.js'

const noteDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

/** Important notes read red; everything else stays neutral. */
const toneClass = (note: Pick<CustomerNote, 'important'>) =>
  note.important
    ? 'border-danger bg-danger-subtle text-danger'
    : 'border-border bg-surface text-foreground'

function useCustomerNotes(customerId: string) {
  return useQuery({
    queryKey: queryKeys.customers.notes(customerId),
    queryFn: () => api.get<CustomerNote[]>(`/customers/${customerId}/notes`),
  })
}

/**
 * Pinned staff notes (t1251), shown right under the customer page header so
 * they can't be missed. Renders nothing when no note is pinned.
 */
export function PinnedNotes({ customerId }: { customerId: string }) {
  const { data: notes } = useCustomerNotes(customerId)
  const pinned = (notes ?? []).filter((n) => n.pinned)
  if (pinned.length === 0) return null
  return (
    <section className="mb-4 space-y-2" aria-label="Pinned notes">
      {pinned.map((note) => (
        <div
          key={note.id}
          className={`flex items-start gap-2 rounded-md border px-3 py-2 text-sm ${toneClass(note)}`}
        >
          {note.important ? (
            <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-label="Important" />
          ) : (
            <Pin className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          )}
          <p className="min-w-0 flex-1 whitespace-pre-wrap">
            <LinkifiedText text={note.body} />
          </p>
        </div>
      ))}
    </section>
  )
}

/**
 * All staff notes on a customer (t1251): several per customer, each with its
 * author and date. Pin one to put it at the top of the page; mark it
 * important to show it in red. Staff-only — nothing here reaches the customer.
 */
export function CustomerNotesCard({
  customerId,
  canWrite,
}: {
  customerId: string
  canWrite: boolean
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [dialog, setDialog] = useState<{ note: CustomerNote | null } | null>(null)
  const [removing, setRemoving] = useState<CustomerNote | null>(null)

  const { data: notes } = useCustomerNotes(customerId)
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.customers.notes(customerId) })

  const toggle = useMutation({
    mutationFn: ({
      note,
      patch,
    }: {
      note: CustomerNote
      patch: Partial<CreateCustomerNoteInput>
    }) => api.patch(`/customers/${customerId}/notes/${note.id}`, patch),
    onSuccess: refresh,
    onError: (error: Error) => toast.error(error.message),
  })

  const remove = useMutation({
    mutationFn: (note: CustomerNote) => api.delete(`/customers/${customerId}/notes/${note.id}`),
    onSuccess: async () => {
      await refresh()
      toast.success('Note deleted')
      setRemoving(null)
    },
    onError: (error: Error) => toast.error(error.message),
  })

  return (
    <Card>
      <CardHeader className="flex items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <StickyNote className="size-4" aria-hidden="true" /> Notes
        </CardTitle>
        {canWrite ? (
          <Button size="sm" variant="secondary" onClick={() => setDialog({ note: null })}>
            <Plus className="size-3.5" aria-hidden="true" /> Add
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="p-0">
        {notes && notes.length > 0 ? (
          <ul className="divide-y divide-border">
            {notes.map((note) => (
              <li
                key={note.id}
                className={`px-4 py-2.5 text-sm ${note.important ? 'bg-danger-subtle text-danger' : ''}`}
              >
                <p className="whitespace-pre-wrap">
                  <LinkifiedText text={note.body} />
                </p>
                <div className="mt-1 flex items-center gap-1">
                  <p className="min-w-0 flex-1 text-2xs opacity-75">
                    {[note.createdByName, noteDate(note.createdAt)].filter(Boolean).join(' · ')}
                    {note.updatedAt !== note.createdAt ? ' · edited' : ''}
                  </p>
                  {canWrite ? (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={note.pinned ? 'Unpin note' : 'Pin note to top'}
                        aria-pressed={note.pinned}
                        onClick={() => toggle.mutate({ note, patch: { pinned: !note.pinned } })}
                      >
                        {note.pinned ? (
                          <PinOff className="size-3.5" aria-hidden="true" />
                        ) : (
                          <Pin className="size-3.5" aria-hidden="true" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={note.important ? 'Clear important' : 'Mark important'}
                        aria-pressed={note.important}
                        onClick={() =>
                          toggle.mutate({ note, patch: { important: !note.important } })
                        }
                      >
                        <AlertTriangle className="size-3.5" aria-hidden="true" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Edit note"
                        onClick={() => setDialog({ note })}
                      >
                        <Pencil className="size-3.5" aria-hidden="true" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Delete note"
                        onClick={() => setRemoving(note)}
                      >
                        <Trash2 className="size-3.5" aria-hidden="true" />
                      </Button>
                    </>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-4 py-3 text-sm text-foreground-muted">
            No notes yet. Only staff see notes — the customer never does.
          </p>
        )}
      </CardContent>

      <NoteDialog
        customerId={customerId}
        state={dialog}
        onClose={() => setDialog(null)}
        onSaved={refresh}
      />
      <Dialog open={!!removing} onOpenChange={(open) => !open && setRemoving(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete this note?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="whitespace-pre-wrap text-sm text-foreground-muted">{removing?.body}</p>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRemoving(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => removing && remove.mutate(removing)}
              disabled={remove.isPending}
            >
              {remove.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

function NoteDialog({
  customerId,
  state,
  onClose,
  onSaved,
}: {
  customerId: string
  state: { note: CustomerNote | null } | null
  onClose: () => void
  onSaved: () => Promise<unknown>
}) {
  const toast = useToast()
  const note = state?.note ?? null
  const [body, setBody] = useState('')
  const [pinned, setPinned] = useState(false)
  const [important, setImportant] = useState(false)

  useEffect(() => {
    if (!state) return
    setBody(note?.body ?? '')
    setPinned(note?.pinned ?? false)
    setImportant(note?.important ?? false)
  }, [state, note])

  const save = useMutation({
    mutationFn: () => {
      const parsed = createCustomerNoteSchema.safeParse({ body, pinned, important })
      if (!parsed.success) {
        throw new Error(parsed.error.issues[0]?.message ?? 'Write something in the note')
      }
      return note
        ? api.patch(`/customers/${customerId}/notes/${note.id}`, parsed.data)
        : api.post(`/customers/${customerId}/notes`, parsed.data)
    },
    onSuccess: async () => {
      await onSaved()
      toast.success(note ? 'Note saved' : 'Note added')
      onClose()
    },
    onError: (error: Error) => toast.error(error.message),
  })

  return (
    <Dialog open={!!state} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{note ? 'Edit note' : 'Add a note'}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Note" required hint="Only staff see this. The customer never does.">
            {(props) => (
              <Textarea
                {...props}
                autoFocus
                rows={5}
                maxLength={2000}
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
            )}
          </Field>
          <div className="flex items-center justify-between rounded-md border border-border p-2.5">
            <span className="text-sm">Pin to top of customer page</span>
            <Switch checked={pinned} onCheckedChange={setPinned} />
          </div>
          <div className="flex items-center justify-between rounded-md border border-border p-2.5">
            <span className="text-sm">Important (shows in red)</span>
            <Switch checked={important} onCheckedChange={setImportant} />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => save.mutate()}
            disabled={!body.trim() || save.isPending}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
